// src/core/identity/ProfileTypes.ts
export type ProfileFont =
  | "Inter" | "Rajdhani" | "Orbitron" | "VT323" | "Gothic" | "Retro" | "Arcade" | "Pixel" | "Horror" | "Terminal"
  | "Ethereal" | "Elven Script" | "Mythic Gothic" | "Aura Round" | "Runic Scroll" | "Ornate Imperial"
  | "Audiowide" | "Chakra Petch" | "Uncial Antiqua" | "Metamorphous";

export type NameStyle = "Solid" | "Gradient" | "Neon" | "POP";

export type Palette =
  | "Cyberpunk" | "Vaporwave" | "Outrun" | "Neon Nights" | "Midnight"
  | "Enchanted Forest" | "Celestial Stardust" | "Ethereal Light" | "Fairy Grove" | "Abyssal Deep"
  | "Custom Solid" | "Custom Gradient";

export type TextureCategory = "Tech" | "Mystical" | "Psychedelic" | "Kawaii" | "Stoner";

export type Texture =
  | "None"
  | "Static" | "Scanlines" | "Glitch Bars" | "VCR Static" | "Hex Grid"
  | "Fairy Dust" | "Ethereal Mist" | "Arcane Runes" | "Runic Grid" | "Starfield"
  | "Matrix Rain"
  | "Psychedelic Wash" | "Acid Wash" | "Liquid Chrome" | "Fractal Zoom" | "Chromatic Aberration"
  | "Kawaii Hearts" | "Cherry Blossom" | "Pastel Sparkles" | "Bubble Pop"
  | "Stoner Plumes" | "Smoke Plumes" | "Lava Lamp" | "Rasta Weave";

export type AvatarDecoration =
  | "None"
  | "Neon Frame" | "Cyber Glitch" | "Neural Circuit" | "Quantum Halo"
  | "Elven Vines" | "Celestial Orbit" | "Angelic Wings" | "Runic Ring"
  | "Cat Ears" | "Halo" | "Flower Crown" | "Pulsing Aura" | "Rainbow Ring" | "Dragon Scales";

export type AuraMode =
  | "None"
  | "Static" | "Pulse Animated" | "Breathe"
  | "Audio-Reactive" | "RGB Color-Cycle" | "Chaotic";

export type ShowcaseBadge =
  | "None" | "Cyber Runner" | "Void Walker" | "Grid Master" | "Data Miner"
  | "Fairy Queen" | "Star Weaver" | "Elven Sage" | "Void Druid";

export type ProfileAnimation = "None" | "Glitch" | "Cyber Surge" | "Neon Overload" | "Void Tear" | "Data Corrupt" | "Plasma Burn" | "Hologram Flicker" | "Liquid Chrome" | "VHS Nightmare" | "Spectral Bloom" | "Ethereal Aura";

export type LFOWaveform = "None" | "Sine" | "Sawtooth" | "Pulse" | "S&H";
export type ColorFilter = "None" | "HSV Inversion" | "Channel Shift" | "Psychodynamic Red";

export type GradientDirection = "90deg" | "180deg" | "270deg" | "0deg" | "45deg" | "135deg" | "225deg" | "315deg";

export interface ProfileCustom {
  fontFamily?: string;
  font: ProfileFont;
  bodyFont?: ProfileFont;
  nameStyle: NameStyle;
  gradientDirection?: GradientDirection;
  /** When true and nameStyle is 'Gradient', the callsign gradient sweeps on hover. */
  gradientAnimated?: boolean;
  palette: Palette;
  color1: string;
  color2: string;
  texture: Texture;
  avatarDecoration: AvatarDecoration;
  auraMode: AuraMode;
  auraColor1: string;
  auraColor2: string;
  statusMessage: string;
  pronouns: string;
  badge: ShowcaseBadge;
  customBadgeIcon?: string;
  customBadgeText?: string;
  customBgColor: string;
  themeColor1?: string;
  themeColor2?: string;
  auraAffectsBanner?: boolean;
  profileAnimation?: ProfileAnimation;
  customStatus?: string;
  textureIntensity?: number;
  textureOpacity?: number;
  /** Animated diagonal light band swept across the texture/banner, tinted by auraColor1. */
  textureNeonSweep?: boolean;
  lfoWaveform?: LFOWaveform;
  lfoRate?: number;
  lfoFrequency?: number;
  colorFilter?: ColorFilter;
  audioGain?: number;
  rgbCycleSpeed?: number;
  chaoticSpeed?: number;
  avatarPosition?: string;
}

export const DEFAULT_CUSTOMIZATION: ProfileCustom = {
  font: 'Inter',
  bodyFont: 'Inter',
  nameStyle: 'Neon',
  gradientDirection: '90deg',
  palette: 'Cyberpunk',
  color1: '#818cf8',
  color2: '#22d3ee',
  texture: 'None',
  avatarDecoration: 'None',
  auraMode: 'None',
  auraColor1: '#22d3ee',
  auraColor2: '#c084fc',
  statusMessage: '',
  pronouns: '',
  badge: 'None',
  customBadgeIcon: '',
  customBadgeText: '',
  customBgColor: '#090d16',
  auraAffectsBanner: false,
  profileAnimation: 'Glitch',
  customStatus: '',
  textureIntensity: 50,
  textureOpacity: 45,
  lfoWaveform: 'None',
  lfoRate: 1.0,
  lfoFrequency: 1.0,
  colorFilter: 'None',
  audioGain: 1.0,
  rgbCycleSpeed: 1.0,
  chaoticSpeed: 1.0,
};
